import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Snackbar } from '@mui/material';
import { Alert } from '@mui/material';

import { getDynamicRoutes } from 'routes';
import { Header, ParadigmNavbar, ParadigmFooter } from 'common';
import { reload, loadPrivacyPolicy, logout, actions as authActions } from 'auth/authSlice';
import { agreeToPrivacyPolicy } from 'user/userSlice';
import { actions as appActions } from 'appSlice';
import { AgreePrivacyPolicyModal } from 'privacyPolicy/AgreePrivacyPolicyModal';
import { NewReferral } from 'referral/NewReferral';

import 'pdfjs-dist/build/pdf.worker.mjs';

const { setAuthToken, setAuthPending, setAuthError } = authActions;

/**
 * Detect if we are running as an external embed (e.g., inside a WordPress page).
 * In that scenario the URL looks like: https://some-wordpress-site.io/external/#/web/referral
 * We must NOT use BrowserRouter (it can't match the WordPress pathname).
 * Instead we use HashRouter as the sole router.
 */
const isExternalEmbed = window.location.pathname.includes('/external')
    || window.location.hash.includes('/web/referral');

function shouldShowParadigmUI(path) {
    if (path && path.includes('web/referral')) {
        return true;
    }
    if (window.location.hash && window.location.hash.includes('web/referral')) {
        return true;
    }
    return false;
}

function App(props) {
    const dispatch = useDispatch();
    const { 
        pending, 
        authorized, 
        userType,
        privacyPolicyLoading,
        privacyPolicyDate,
        privacyPolicyContent,
        requirePrivacyPolicy
    } = useSelector(state => state.auth);
    const { message, messageType } = useSelector(state => state.app);

    useEffect(() => {
        const query = window.location.search;
        const params = new URLSearchParams(query);
        const path = window.location.pathname;
        dispatch(loadPrivacyPolicy());
        if (params.has("authError")) 
            dispatch(setAuthError(params.get("authError")));
        if (params.has('token')) {
            dispatch(setAuthToken(params.get('token')));
        } else if (path.includes('/auth/') || path.includes('/web/')|| path.includes('/external')) {
            dispatch(setAuthPending(false));
        } else {
            dispatch(reload()); 
        }
    }, [dispatch]);

    const userRoutes = getDynamicRoutes(userType, authorized, pending);

    const showParadigmUI = shouldShowParadigmUI(window.location.pathname);

    const handleClickDisagree = () => {
        dispatch(logout());
    }

    const handleClickAgree = () => {
        dispatch(agreeToPrivacyPolicy());
    }
    
    // ── External embed mode: HashRouter only, just the referral page ──
    // No ParadigmNavbar/Footer — the WordPress host page provides its own chrome
    if (isExternalEmbed) {
        return (
            <HashRouter>
                <Container style={{ padding: '0px' }} maxWidth={false}>
                    <Routes>
                        <Route path="/web/referral" element={<NewReferral />} />
                    </Routes>
                    <Snackbar open={!!message} autoHideDuration={messageType === 'error' ? null : 5000} onClose={() => dispatch(appActions.clearMessage())}>
                        <Alert onClose={() => dispatch(appActions.clearMessage())} severity={messageType}>
                            {message}
                        </Alert>
                    </Snackbar>
                </Container>
            </HashRouter>
        );
    }

    // ── Normal mode: full app with BrowserRouter ──
    return (
        <BrowserRouter>
            { authorized && <Header /> }
            { showParadigmUI && <ParadigmNavbar />}
            <Container style={{ padding: '0px' }} maxWidth={false}>
                <AgreePrivacyPolicyModal 
                        content={privacyPolicyContent} 
                        version={privacyPolicyDate} 
                        handleClickAgree={handleClickAgree} 
                        handleClickDisagree={handleClickDisagree} 
                        handleCloseModal={handleClickDisagree} 
                        openModal={requirePrivacyPolicy}
                        isLoading={privacyPolicyLoading}/>
                {userRoutes}
                <Snackbar open={!!message} autoHideDuration={messageType === 'error' ? null : 5000} onClose={() => dispatch(appActions.clearMessage())}>
                    <Alert onClose={() => dispatch(appActions.clearMessage())} severity={messageType}>
                        {message}
                    </Alert>
                </Snackbar>
            </Container>
            { showParadigmUI && <ParadigmFooter />}
        </BrowserRouter>
    );
}

export default App;
